import {
  type WorkflowDefinition,
  type WorkflowDefinitionEdge,
  type WorkflowEdgeData,
  WorkflowNodeType,
} from './workflow-definition';

/**
 * Folding and focusing the canvas. A fold hides what one step's output leads to, the way the
 * output chips at the foot of a card do; these work on the definition, so the canvas lays out only
 * what is left showing.
 */

/**
 * One output of one step: `if-1:true`, `if-1:false`, `menu-1:option:security` for a Phone menu
 * option, or `say-1:next` for a single-output step. Takes an `if` answer or the edge data.
 */
export const outputKey = (sourceId: string, output?: boolean | WorkflowEdgeData) => {
  const { condition, option } =
    typeof output === 'boolean' ? { condition: output } : (output ?? {});
  if (typeof condition === 'boolean') return `${sourceId}:${condition}`;
  if (option) return `${sourceId}:option:${option}`;
  return `${sourceId}:next`;
};

const edgeOutputKey = ({ source, data }: WorkflowDefinitionEdge) => outputKey(source, data);

const rootId = (definition: WorkflowDefinition) =>
  (
    definition.nodes.find(({ type }) => type === WorkflowNodeType.Trigger) ??
    definition.nodes[0]
  )?.id;

/** Every step reachable from `startIds` along edges, skipping the outputs in `folded`. */
const reach = (
  definition: WorkflowDefinition,
  startIds: string[],
  folded: ReadonlySet<string> = new Set(),
): Set<string> => {
  const seen = new Set(startIds);
  let frontier = startIds;
  while (frontier.length) {
    const next = definition.edges
      .filter(
        (edge) =>
          frontier.includes(edge.source) &&
          !folded.has(edgeOutputKey(edge)) &&
          !seen.has(edge.target),
      )
      .map(({ target }) => target);
    next.forEach((id) => seen.add(id));
    frontier = [...new Set(next)];
  }
  return seen;
};

/** The steps on any path from the trigger to `nodeId`, not counting `nodeId` itself. */
export const ancestorIds = (definition: WorkflowDefinition, nodeId: string): Set<string> => {
  const seen = new Set<string>();
  let frontier = [nodeId];
  while (frontier.length) {
    const parents = definition.edges
      .filter(({ target, source }) => frontier.includes(target) && !seen.has(source))
      .map(({ source }) => source);
    parents.forEach((id) => seen.add(id));
    frontier = [...new Set(parents)];
  }
  seen.delete(nodeId);
  return seen;
};

/**
 * The steps left showing: those reachable from the trigger without passing a folded output. A
 * step the trigger can't reach at all stays showing, so a broken draft never loses a step.
 */
export const visibleStepIds = (
  definition: WorkflowDefinition,
  folded: ReadonlySet<string>,
): Set<string> => {
  const root = rootId(definition);
  if (!root) return new Set();
  const reachable = reach(definition, [root]);
  const shown = reach(definition, [root], folded);
  definition.nodes.forEach(({ id }) => {
    if (!reachable.has(id)) shown.add(id);
  });
  return shown;
};

/** The definition with only the showing steps, and the connections between them. */
export const withoutFoldedSteps = (
  definition: WorkflowDefinition,
  folded: ReadonlySet<string>,
): WorkflowDefinition => {
  if (!folded.size) return definition;
  const shown = visibleStepIds(definition, folded);
  return {
    ...definition,
    nodes: definition.nodes.filter(({ id }) => shown.has(id)),
    edges: definition.edges.filter(
      (edge) =>
        shown.has(edge.source) && shown.has(edge.target) && !folded.has(edgeOutputKey(edge)),
    ),
  };
};

/**
 * How many steps each folded output on screen hides — the "+N" on its chip. A step that two folded
 * outputs both lead to is counted once, on the first of them in stored order, so the counts add up
 * to the steps actually hidden.
 */
export const hiddenStepCounts = (
  definition: WorkflowDefinition,
  folded: ReadonlySet<string>,
): Map<string, number> => {
  const counts = new Map<string, number>();
  if (!folded.size) return counts;
  const shown = visibleStepIds(definition, folded);
  const counted = new Set<string>();
  for (const edge of definition.edges) {
    const key = edgeOutputKey(edge);
    if (counts.has(key) || !folded.has(key) || !shown.has(edge.source)) continue;
    const targets = definition.edges
      .filter((other) => edgeOutputKey(other) === key)
      .map(({ target }) => target);
    const hidden = [...reach(definition, targets)].filter(
      (id) => !shown.has(id) && !counted.has(id),
    );
    hidden.forEach((id) => counted.add(id));
    counts.set(key, hidden.length);
  }
  return counts;
};

/**
 * Every output that leads somewhere, except the trigger's: what Collapse all folds, leaving the
 * trigger and the first step showing.
 */
export const foldableOutputs = (definition: WorkflowDefinition): Set<string> => {
  const root = rootId(definition);
  return new Set(definition.edges.filter(({ source }) => source !== root).map(edgeOutputKey));
};

/** Unfolds every output on the way from the trigger to `nodeId`, so it shows. */
export const revealStep = (
  definition: WorkflowDefinition,
  folded: ReadonlySet<string>,
  nodeId: string,
): ReadonlySet<string> => {
  const onPath = ancestorIds(definition, nodeId);
  onPath.add(nodeId);
  const next = new Set(folded);
  definition.edges.forEach((edge) => {
    if (onPath.has(edge.source) && onPath.has(edge.target)) next.delete(edgeOutputKey(edge));
  });
  // the same set when nothing hid it, so the canvas isn't laid out again for nothing
  return next.size === folded.size ? folded : next;
};

/** One step on the way to a focused step, and which output of the step before it led here. */
export interface FocusPathStep {
  nodeId: string;
  /** `true` / `false` when the step before is an `if`; absent for the trigger or a plain step. */
  via?: boolean;
}

/**
 * The way from the trigger to `nodeId`, in order — the steps that run before it, and the answer
 * each `if` gave. Follows the first connection into each step, so a step reached two ways shows one.
 */
export const focusPath = (definition: WorkflowDefinition, nodeId: string): FocusPathStep[] => {
  const path: FocusPathStep[] = [];
  const seen = new Set<string>();
  let current: string | undefined = nodeId;
  while (current && !seen.has(current)) {
    seen.add(current);
    const incoming = definition.edges.find(({ target }) => target === current);
    path.unshift({
      nodeId: current,
      ...(typeof incoming?.data?.condition === 'boolean'
        ? { via: incoming.data.condition }
        : {}),
    });
    current = incoming?.source;
  }
  return path;
};

/** What a focus keeps lit: the way from the trigger to the step, and the step. */
export const focusedStepIds = (definition: WorkflowDefinition, nodeId: string): Set<string> =>
  new Set(focusPath(definition, nodeId).map((step) => step.nodeId));

/** The step above `nodeId` — the first connection into it — for the ↑ key. */
export const parentStepId = (definition: WorkflowDefinition, nodeId: string) =>
  definition.edges.find(({ target }) => target === nodeId)?.source ?? null;

/** The steps right after `nodeId`, true before false, as the outputs sit left to right. */
export const childStepIds = (definition: WorkflowDefinition, nodeId: string): string[] =>
  definition.edges
    .filter(({ source }) => source === nodeId)
    .sort((a, b) => Number(b.data?.condition === true) - Number(a.data?.condition === true))
    .map(({ target }) => target);
